import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, X, Plus } from "lucide-react";
import api from "../../lib/api";

/* Scheduled safety check-ins with grace-period escalation. */
const STATUS_STYLE = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  due: "bg-amber-50 text-amber-800 border-amber-300 animate-pulse",
  escalated: "bg-rose-50 text-rose-700 border-rose-300",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  missed: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function GuardianCheckins() {
  const [items, setItems] = useState(null);
  const [circles, setCircles] = useState([]);
  const [form, setForm] = useState({ circle_id: "", label: "Safety check-in", scheduled_at: "", grace_minutes: 30 });
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    api.get("/guardian/checkins").then((r) => setItems(r.data.items)).catch(() => setItems([]));
    api.get("/guardian/circles").then((r) => setCircles(r.data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.circle_id || !form.scheduled_at) { toast.error("Pick a circle and a time"); return; }
    try {
      await api.post("/guardian/checkins", { ...form, grace_minutes: Number(form.grace_minutes), scheduled_at: new Date(form.scheduled_at).toISOString() });
      toast.success("Check-in scheduled"); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); }
  };
  const complete = async (id) => {
    try { await api.post(`/guardian/checkins/${id}/complete`, {}); toast.success("Checked in — your circle was told you're safe"); load(); }
    catch { toast.error("Could not check in"); }
  };
  const cancel = async (id) => {
    try { await api.delete(`/guardian/checkins/${id}`); load(); } catch { toast.error("Could not cancel"); }
  };

  if (!items) return <p className="text-sm text-zinc-400">Loading…</p>;

  return (
    <div data-testid="guardian-checkins">
      <p className="text-xs text-zinc-500 mb-4">Schedule a check-in. If you don't confirm within the grace period, your <b>emergency contacts and guardians</b> are calmly informed — with your last-known status, never alarmist language.</p>

      {items.length === 0 && <p className="text-sm text-zinc-400 border border-dashed border-zinc-300 rounded-xl px-4 py-8 text-center" data-testid="guardian-checkins-empty">No check-ins scheduled.</p>}

      <div className="space-y-2.5 mb-4">
        {items.map((c) => (
          <div key={c.id} className="border border-zinc-200 bg-white rounded-xl px-4 py-3 flex flex-wrap items-center gap-2.5" data-testid="guardian-checkin-row">
            <CalendarClock className="w-4 h-4 text-blue-600 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-800">{c.label}</div>
              <div className="text-[11px] text-zinc-500">{new Date(c.scheduled_at).toLocaleString()} · grace {c.grace_minutes} min</div>
            </div>
            <span className={`ml-auto rounded-full border px-2.5 py-0.5 text-[10px] font-800 uppercase ${STATUS_STYLE[c.status] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`} data-testid="guardian-checkin-status">{c.status}</span>
            {["scheduled", "due", "escalated"].includes(c.status) && (
              <button onClick={() => complete(c.id)} data-testid={`guardian-checkin-complete-${c.id}`}
                className="inline-flex items-center gap-1 bg-emerald-600 text-white text-xs font-800 rounded-full px-3.5 py-1.5 hover:bg-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> I'm safe</button>
            )}
            {["scheduled", "due"].includes(c.status) && (
              <button onClick={() => cancel(c.id)} data-testid={`guardian-checkin-cancel-${c.id}`} className="text-zinc-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>

      {!showForm ? (
        <button onClick={() => setShowForm(true)} data-testid="guardian-checkin-new-btn"
          className="inline-flex items-center gap-1.5 bg-blue-700 text-white text-sm font-800 rounded-full px-5 py-2.5 hover:bg-blue-800"><Plus className="w-4 h-4" /> Schedule a check-in</button>
      ) : (
        <div className="border border-zinc-200 rounded-2xl p-4 bg-white space-y-3" data-testid="guardian-checkin-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <select value={form.circle_id} onChange={(e) => setForm({ ...form, circle_id: e.target.value })} data-testid="guardian-checkin-circle" className="border border-zinc-300 rounded-xl px-3 py-2.5 text-sm bg-white">
              <option value="">Who to inform (circle)…</option>
              {circles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="guardian-checkin-label" placeholder="Label (e.g. Arrived at hotel)" className="border border-zinc-300 rounded-xl px-3 py-2.5 text-sm" />
            <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="guardian-checkin-when" className="border border-zinc-300 rounded-xl px-3 py-2.5 text-sm" />
            <select value={form.grace_minutes} onChange={(e) => setForm({ ...form, grace_minutes: e.target.value })} data-testid="guardian-checkin-grace" className="border border-zinc-300 rounded-xl px-3 py-2.5 text-sm bg-white">
              {[[10, "10 min grace"], [30, "30 min grace"], [60, "1 hour grace"], [120, "2 hours grace"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={create} data-testid="guardian-checkin-save" className="bg-blue-700 text-white text-sm font-800 rounded-full px-5 py-2 hover:bg-blue-800">Schedule</button>
            <button onClick={() => setShowForm(false)} className="text-sm text-zinc-500 px-3">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
